<template>
  <div class="flow-canvas-container" ref="containerRef" tabindex="0"
    @keydown="onKeyDown"
    @mousedown="onCanvasMouseDown"
    @mousemove="onCanvasMouseMove"
    @mouseup="onCanvasMouseUp"
    @mouseleave="onCanvasMouseUp"
    @wheel.prevent="onWheel"
    @dragover.prevent @drop.prevent="onDrop"
    @click.self="onCanvasClick"
  >
    <svg :width="containerW" :height="containerH" class="flow-svg" :style="{ cursor: panning ? 'grabbing' : 'default' }">
      <rect width="100%" height="100%" fill="#f9fafb" @click="onCanvasClick" />
      <!-- Grid -->
      <defs>
        <pattern id="gridPattern" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e8ecf0" stroke-width="0.5"/>
        </pattern>
        <marker id="arrowhead" markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
          <polygon points="0 0, 10 4, 0 8" fill="#9ca3af"/>
        </marker>
        <filter id="nodeShadow">
          <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="#00000020"/>
        </filter>
        <filter id="nodeShadowHover">
          <feDropShadow dx="0" dy="2" stdDeviation="4" flood-color="#00000025"/>
        </filter>
      </defs>
      <rect width="100%" height="100%" fill="url(#gridPattern)" />

      <!-- Edges -->
      <g class="edges-layer">
        <g v-for="edge in edges" :key="edge.id">
          <!-- invisible wider hit area for easier clicking -->
          <polyline
            :points="getPolylinePoints(edge)"
            fill="none"
            stroke="transparent" stroke-width="16"
            class="flow-edge-hit"
            @click.stop="selectEdge(edge.id)"
            @dblclick.stop="addWaypoint(edge, $event)"
            @contextmenu.prevent.stop="onEdgeContextMenu($event, edge)"
            :style="{ cursor: 'pointer' }"
          />
          <polyline
            :points="getPolylinePoints(edge)"
            fill="none"
            :stroke="edge.id === selectedEdgeId ? '#059669' : '#9ca3af'"
            :stroke-width="edge.id === selectedEdgeId ? 3 : 2"
            marker-end="url(#arrowhead)"
            class="flow-edge"
            :class="{ 'selected-edge': edge.id === selectedEdgeId }"
            style="pointer-events:none"
          />
          <!-- Edge label: displayName first, then showName, then label -->
          <text v-if="getEdgeDisplayLabel(edge)"
            :x="getEdgeLabelPos(edge).x"
            :y="getEdgeLabelPos(edge).y"
            text-anchor="middle" font-size="11"
            :fill="edge.id === selectedEdgeId ? '#059669' : '#6b7280'"
            style="pointer-events:none"
          >{{ getEdgeDisplayLabel(edge) }}</text>
          <!-- Waypoint control points (only shown when edge is selected) -->
          <template v-if="edge.id === selectedEdgeId && edge.waypoints && edge.waypoints.length > 0">
            <circle v-for="(wp, wi) in edge.waypoints" :key="'wp_'+wi"
              :cx="wp.x" :cy="wp.y" r="5"
              fill="#fff" stroke="#059669" stroke-width="2"
              class="waypoint-handle"
              @mousedown.stop="startWaypointDrag(edge, wi, $event)"
              @contextmenu.prevent.stop="deleteWaypoint(edge, wi, $event)"
              :style="{ cursor: 'move' }"
            />
          </template>
        </g>
        <polyline v-if="connectingFrom"
          :points="getTempPolylinePoints()"
          fill="none"
          stroke="#10b981" stroke-width="2" stroke-dasharray="5,4"
          marker-end="url(#arrowhead)"
        />
      </g>

      <!-- Nodes -->
      <g class="nodes-layer">
        <g v-for="node in nodes" :key="node.id"
          :transform="`translate(${node.x}, ${node.y})`"
          @mousedown.stop="onNodeMouseDown($event, node)"
          @click.stop="selectNode(node.id)"
          @dblclick.stop="editNodeProps(node)"
          class="flow-node-group"
          :class="{ selected: node.id === selectedId, connecting: !!connectingFrom, 'connect-target': connectingHover === node.id, dragging: dragging === node.id }"
          :style="{ cursor: connectingFrom ? 'crosshair' : 'default' }"
        >
          <!-- shape: white bg + green border -->
          <template v-if="node.typeKey === 'start' || node.typeKey === 'end'">
            <rect :width="node.width" :height="node.height" :rx="node.height/2"
              :fill="selectedId === node.id ? '#f0fdf6' : '#fff'"
              :stroke="selectedId === node.id ? '#059669' : '#10b981'"
              stroke-width="2"
              :filter="selectedId === node.id ? 'url(#nodeShadowHover)' : 'url(#nodeShadow)'" />
          </template>
          <template v-else-if="node.typeKey === 'condition'">
            <polygon :points="`${node.width/2},0 ${node.width},${node.height/2} ${node.width/2},${node.height} 0,${node.height/2}`"
              :fill="selectedId === node.id ? '#f0fdf6' : '#fff'"
              :stroke="selectedId === node.id ? '#059669' : '#10b981'"
              stroke-width="2"
              :filter="'url(#nodeShadow)'" />
          </template>
          <template v-else>
            <rect :width="node.width" :height="node.height" :rx="6"
              :fill="selectedId === node.id ? '#f0fdf6' : '#fff'"
              :stroke="selectedId === node.id ? '#059669' : '#10b981'"
              stroke-width="2"
              :filter="'url(#nodeShadow)'" />
          </template>
          <!-- icon (24x24 viewBox) -->
          <g :transform="`translate(${(node.width - 24) / 2}, 7)`" style="pointer-events:none">
            <!-- 开始: 圆环+右箭头 -->
            <g v-if="node.typeKey === 'start'">
              <circle cx="12" cy="12" r="10" fill="none" stroke="#10b981" stroke-width="2"/>
              <polyline points="8,12 14,8 20,12 14,16" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <!-- 审批: 人形 -->
            <g v-else-if="node.typeKey === 'approval'">
              <circle cx="12" cy="7" r="4" fill="none" stroke="#10b981" stroke-width="2"/>
              <path d="M4 22c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round"/>
            </g>
            <!-- 条件: 菱形+问号 -->
            <g v-else-if="node.typeKey === 'condition'">
              <rect x="4" y="4" width="16" height="16" rx="1" fill="none" stroke="#10b981" stroke-width="2" transform="rotate(45 12 12)"/>
              <path d="M11 10.5V10a2.5 2.5 0 015 0c0 1.5-2 2-2 3h2" fill="none" stroke="#10b981" stroke-width="1.8" stroke-linecap="round"/>
              <circle cx="12" cy="18" r="1.2" fill="#10b981"/>
            </g>
            <!-- 分流: 分支双箭头 -->
            <g v-else-if="node.typeKey === 'split'">
              <line x1="2" y1="12" x2="9" y2="12" stroke="#10b981" stroke-width="2" stroke-linecap="round"/>
              <polyline points="6,8 10,12 6,16" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="10" y1="7" x2="19" y2="7" stroke="#10b981" stroke-width="2" stroke-linecap="round"/>
              <line x1="10" y1="17" x2="19" y2="17" stroke="#10b981" stroke-width="2" stroke-linecap="round"/>
              <polyline points="16,4 20,7 16,10" fill="none" stroke="#10b981" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="16,14 20,17 16,20" fill="none" stroke="#10b981" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <!-- 合流: 汇聚双箭头 -->
            <g v-else-if="node.typeKey === 'join'">
              <line x1="2" y1="12" x2="9" y2="12" stroke="#10b981" stroke-width="2" stroke-linecap="round"/>
              <polyline points="6,8 10,12 6,16" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="2" y1="7" x2="9" y2="7" stroke="#10b981" stroke-width="2" stroke-linecap="round"/>
              <line x1="2" y1="17" x2="9" y2="17" stroke="#10b981" stroke-width="2" stroke-linecap="round"/>
              <polyline points="6,4 2,7 6,10" fill="none" stroke="#10b981" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="6,14 2,17 6,20" fill="none" stroke="#10b981" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <!-- 通知: 信封 -->
            <g v-else-if="node.typeKey === 'notify'">
              <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="#10b981" stroke-width="2"/>
              <polyline points="3,7 12,14 21,7" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <!-- 结束: 双圈粗环 -->
            <g v-else-if="node.typeKey === 'end'">
              <circle cx="12" cy="12" r="10" fill="none" stroke="#10b981" stroke-width="2"/>
              <circle cx="12" cy="12" r="6" fill="#10b981"/>
            </g>
            <!-- 子流程: 叠层方框+加号 -->
            <g v-else-if="node.typeKey === 'subprocess'">
              <rect x="4" y="3" width="16" height="15" rx="3" fill="none" stroke="#10b981" stroke-width="2"/>
              <rect x="6" y="5" width="16" height="15" rx="3" fill="none" stroke="#10b981" stroke-width="1.5" stroke-dasharray="2,1"/>
              <line x1="12" y1="9" x2="12" y2="16" stroke="#10b981" stroke-width="2" stroke-linecap="round"/>
              <line x1="8.5" y1="12.5" x2="15.5" y2="12.5" stroke="#10b981" stroke-width="2" stroke-linecap="round"/>
            </g>
            <!-- SQL插件: 数据库柱体 -->
            <g v-else-if="node.typeKey === 'sqlplugin'">
              <ellipse cx="12" cy="5" rx="9" ry="3" fill="none" stroke="#10b981" stroke-width="2"/>
              <path d="M3 5v12c0 1.7 4 3 9 3s9-1.3 9-3V5" fill="none" stroke="#10b981" stroke-width="2"/>
              <path d="M3 11c0 1.7 4 3 9 3s9-1.3 9-3" fill="none" stroke="#10b981" stroke-width="1.5"/>
              <path d="M3 17c0 1.7 4 3 9 3s9-1.3 9-3" fill="none" stroke="#10b981" stroke-width="1.5"/>
            </g>
            <!-- WebApi插件: 地球 -->
            <g v-else-if="node.typeKey === 'webplugin'">
              <circle cx="12" cy="12" r="10" fill="none" stroke="#10b981" stroke-width="2"/>
              <ellipse cx="12" cy="12" rx="4.5" ry="10" fill="none" stroke="#10b981" stroke-width="1.2"/>
              <line x1="2" y1="12" x2="22" y2="12" stroke="#10b981" stroke-width="1.2"/>
              <circle cx="12" cy="12" r="2" fill="#10b981"/>
            </g>
          </g>
          <!-- label -->
          <text :x="node.width / 2" :y="node.height - 8"
            text-anchor="middle" font-size="11" fill="#374151" font-weight="600"
            style="pointer-events:none"
          >{{ node.props.stepName || node.name }}</text>
          <!-- 节点中心连线锚点（hover时显示绿点，拖拽超过阈值才触发连线） -->
          <circle :cx="node.width / 2" :cy="node.height / 2" r="14"
            fill="transparent" class="connect-center-hotzone"
            style="cursor:crosshair"
            title="从此处拖拽创建连线（ESC取消）"
          />
          <circle :cx="node.width / 2" :cy="node.height / 2" r="5"
            fill="#10b981" stroke="#fff" stroke-width="2"
            class="connect-center-dot" style="pointer-events:none"
          />
        </g>
      </g>
    </svg>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { nodeTypes } from './flowState.js'

const props = defineProps({
  nodes: { type: Array, default: () => [] },
  edges: { type: Array, default: () => [] },
  selectedId: { type: String, default: null },
  selectedEdgeId: { type: String, default: null },
  connectingFrom: { type: String, default: null },
  scale: { type: Number, default: 1 }
})
const emit = defineEmits(['selectNode', 'clearSelection', 'moveNode', 'addNode', 'startConnect', 'finishConnect', 'cancelConnect', 'deleteSelected', 'selectEdge', 'editNode', 'editEdge', 'updateScale', 'updateWaypoints'])

const containerRef = ref(null)
const containerW = ref(800); const containerH = ref(600)
const offsetX = ref(0); const offsetY = ref(0)
const panning = ref(false); const panStart = ref({ x: 0, y: 0 })
const dragging = ref(null); const dragStart = ref({ x: 0, y: 0, nx: 0, ny: 0 })
const waypointDragging = ref(null) // { edgeId, waypointIndex }
const connectingX = ref(0); const connectingY = ref(0)
const connectingHover = ref(null) // 连线模式下鼠标悬停的目标节点ID

// 连线拖拽阈值机制：在中心热区按下后需拖拽超过15px才触发连线
const MIN_DRAG_DIST = 15
const connectPending = ref(null) // { nodeId, startClientX, startClientY }

/** 将 clientX/clientY 转换为 SVG 画布坐标（考虑容器偏移和平移偏移） */
function clientToSvg(e) {
  if (!containerRef.value) return { x: e.clientX, y: e.clientY }
  const r = containerRef.value.getBoundingClientRect()
  return { x: e.clientX - r.left - offsetX.value, y: e.clientY - r.top - offsetY.value }
}

function getNodePort(nodeId, side) {
  const node = props.nodes.find(n => n.id === nodeId)
  if (!node) return { x: 0, y: 0 }
  switch (side) {
    case 'bottom': return { x: node.x + node.width / 2, y: node.y + node.height }
    case 'top': return { x: node.x + node.width / 2, y: node.y }
    case 'right': return { x: node.x + node.width, y: node.y + node.height / 2 }
    case 'left': return { x: node.x, y: node.y + node.height / 2 }
    default: return { x: node.x + node.width / 2, y: node.y + node.height / 2 }
  }
}

function getPolylinePoints(edge) {
  const f = props.nodes.find(n => n.id === edge.from)
  const t = props.nodes.find(n => n.id === edge.to)
  if (!f || !t) return ''
  const dx = (t.x + t.width / 2) - (f.x + f.width / 2)
  const dy = (t.y + t.height / 2) - (f.y + f.height / 2)
  let out, inn
  if (Math.abs(dy) > Math.abs(dx)) {
    if (dy > 0) { out = getNodePort(edge.from, 'bottom'); inn = getNodePort(edge.to, 'top') }
    else { out = getNodePort(edge.from, 'top'); inn = getNodePort(edge.to, 'bottom') }
  } else {
    if (dx > 0) { out = getNodePort(edge.from, 'right'); inn = getNodePort(edge.to, 'left') }
    else { out = getNodePort(edge.from, 'left'); inn = getNodePort(edge.to, 'right') }
  }
  // 如果有手动设置的控制点（waypoints），使用控制点
  if (edge.waypoints && edge.waypoints.length > 0) {
    const wps = edge.waypoints.map(wp => `${wp.x},${wp.y}`).join(' ')
    return `${out.x},${out.y} ${wps} ${inn.x},${inn.y}`
  }
  // 否则自动计算折线
  if (Math.abs(out.x - inn.x) < 5 || Math.abs(out.y - inn.y) < 5) return `${out.x},${out.y} ${inn.x},${inn.y}`
  const gap = dy > 0 ? 20 : -20
  return `${out.x},${out.y} ${out.x},${out.y + gap} ${inn.x},${out.y + gap} ${inn.x},${inn.y}`
}

/** 获取连线显示标签：displayName > showName > 条件分支文本 */
function getEdgeDisplayLabel(edge) {
  if (edge.props?.displayName) return edge.props.displayName
  if (edge.props?.showName) return edge.props.showName
  // 条件节点的出线显示"成立/不成立"
  const srcNode = props.nodes.find(n => n.id === edge.from)
  if (srcNode && srcNode.nodeType === 3) {
    return edge.props?.conditionBranch === '0' ? '不成立' : '成立'
  }
  if (edge.label) return edge.label
  return ''
}

function getEdgeLabelPos(edge) {
  const f = props.nodes.find(n => n.id === edge.from)
  const t = props.nodes.find(n => n.id === edge.to)
  if (!f || !t) return { x: 0, y: 0 }
  return { x: (f.x + f.width / 2 + t.x + t.width / 2) / 2, y: (f.y + f.height / 2 + t.y + t.height / 2) / 2 - 10 }
}

function getTempPolylinePoints() {
  if (!props.connectingFrom) return ''
  const fn = props.nodes.find(n => n.id === props.connectingFrom)
  if (!fn) return ''
  const cx = fn.x + fn.width / 2; const cy = fn.y + fn.height / 2
  return `${cx},${cy} ${connectingX.value},${connectingY.value}`
}

function onCanvasMouseDown(e) {
  const t = e.target
  if (t === e.currentTarget || t.tagName === 'svg' || (t.tagName === 'rect' && t.getAttribute('fill')?.includes('url'))) {
    panning.value = true
    panStart.value = { x: e.clientX - offsetX.value, y: e.clientY - offsetY.value }
  }
}
function onCanvasMouseMove(e) {
  if (dragging.value) {
    const svg = clientToSvg(e)
    emit('moveNode', dragging.value, dragStart.value.nx + svg.x - dragStart.value.sx, dragStart.value.ny + svg.y - dragStart.value.sy)
  }
  if (waypointDragging.value) {
    const svg = clientToSvg(e)
    const edge = props.edges.find(e => e.id === waypointDragging.value.edgeId)
    if (edge && edge.waypoints) {
      edge.waypoints[waypointDragging.value.waypointIndex] = { x: svg.x, y: svg.y }
    }
  }
  if (panning.value) { offsetX.value = e.clientX - panStart.value.x; offsetY.value = e.clientY - panStart.value.y }
  // 连线拖拽阈值检查：拖拽超过15px才真正触发连线
  if (connectPending.value) {
    const dx = e.clientX - connectPending.value.startClientX
    const dy = e.clientY - connectPending.value.startClientY
    if (Math.sqrt(dx * dx + dy * dy) >= MIN_DRAG_DIST) {
      emit('startConnect', connectPending.value.nodeId)
      connectPending.value = null
    }
    return
  }
  if (props.connectingFrom) {
    let svgPt = clientToSvg(e)
    // 吸附逻辑：靠近目标节点时吸附到最近锚点
    let hoverTarget = null
    for (const n of props.nodes) {
      if (svgPt.x >= n.x && svgPt.x <= n.x + n.width && svgPt.y >= n.y && svgPt.y <= n.y + n.height) {
        if (n.id !== props.connectingFrom) { hoverTarget = n.id; break }
      }
    }
    if (hoverTarget) {
      const tn = props.nodes.find(nn => nn.id === hoverTarget)
      if (tn) {
        // 吸附到最近的边中点
        const tcx = tn.x + tn.width / 2; const tcy = tn.y + tn.height / 2
        const dists = [
          { x: tn.x, y: tcy },
          { x: tn.x + tn.width, y: tcy },
          { x: tcx, y: tn.y },
          { x: tcx, y: tn.y + tn.height }
        ]
        let best = dists[0]; let minD = 1e9
        for (const d of dists) { const dd = (svgPt.x - d.x) ** 2 + (svgPt.y - d.y) ** 2; if (dd < minD) { minD = dd; best = d } }
        svgPt = best
      }
    }
    connectingX.value = svgPt.x; connectingY.value = svgPt.y
    connectingHover.value = hoverTarget
  }
}
function onCanvasMouseUp(e) {
  if (dragging.value) dragging.value = null
  if (waypointDragging.value) {
    const edge = props.edges.find(e => e.id === waypointDragging.value.edgeId)
    if (edge && edge.waypoints) {
      emit('updateWaypoints', edge.id, [...edge.waypoints])
    }
    waypointDragging.value = null
  }
  // 连线拖拽未达阈值：只是点击，取消
  if (connectPending.value) { connectPending.value = null; return }
  if (props.connectingFrom) {
    const svg = clientToSvg(e)
    let hitTarget = false
    for (const n of props.nodes) {
      if (svg.x >= n.x && svg.x <= n.x + n.width && svg.y >= n.y && svg.y <= n.y + n.height) {
        if (n.id !== props.connectingFrom) { emit('finishConnect', n.id); hitTarget = true; break }
      }
    }
    if (!hitTarget) emit('cancelConnect')
  }
  connectingHover.value = null
  panning.value = false
}

/** 开始拖拽折线控制点 */
function startWaypointDrag(edge, waypointIndex, e) {
  waypointDragging.value = { edgeId: edge.id, waypointIndex }
}

/** 双击连线添加折线控制点 */
function addWaypoint(edge, e) {
  const svg = clientToSvg(e)
  if (!edge.waypoints) edge.waypoints = []
  edge.waypoints.push({ x: svg.x, y: svg.y })
  emit('updateWaypoints', edge.id, [...edge.waypoints])
}

/** 右键删除折线控制点 */
function deleteWaypoint(edge, waypointIndex, e) {
  e.preventDefault()
  e.stopPropagation()
  if (edge.waypoints) {
    edge.waypoints.splice(waypointIndex, 1)
    emit('updateWaypoints', edge.id, [...edge.waypoints])
  }
}

/** 连线右键菜单 */
function onEdgeContextMenu(e, edge) {
  // 先选中连线
  emit('selectEdge', edge.id)
  // 创建右键菜单
  const old = document.querySelector('.edge-ctx-menu')
  if (old) old.remove()
  const menu = document.createElement('div')
  menu.className = 'edge-ctx-menu'
  menu.innerHTML = `
    <div class="ctx-item" data-action="edit"><i class="fas fa-edit"></i> 编辑属性</div>
    <div class="ctx-item ctx-danger" data-action="delete"><i class="fas fa-trash"></i> 删除连线</div>
  `
  menu.style.cssText = `position:fixed;left:${e.clientX}px;top:${e.clientY}px;z-index:9999;background:#fff;border:1px solid #e5e7eb;border-radius:6px;box-shadow:0 4px 16px rgba(0,0,0,0.12);padding:4px 0;min-width:140px;font-size:12px;`
  menu.addEventListener('click', (ev) => {
    const act = ev.target.closest('.ctx-item')?.dataset?.action
    if (act === 'edit') { emit('editEdge', edge) }
    if (act === 'delete') { emit('deleteSelected'); emit('selectEdge', null) }
    menu.remove()
  })
  // 点击其他地方关闭
  const close = (ev) => { if (!menu.contains(ev.target)) { menu.remove(); document.removeEventListener('click', close) } }
  setTimeout(() => document.addEventListener('click', close), 0)
  document.body.appendChild(menu)
}
function onNodeMouseDown(e, node) {
  // 如果在中心连线锚点上按下 → 进入"待确认"状态，需拖拽超过阈值才触发连线
  if (e.target.classList.contains('connect-center-hotzone')) {
    connectPending.value = { nodeId: node.id, startClientX: e.clientX, startClientY: e.clientY }
    return
  }
  // 开始拖拽移动节点
  dragging.value = node.id
  const svg = clientToSvg(e)
  dragStart.value = { x: e.clientX, y: e.clientY, nx: node.x, ny: node.y, sx: svg.x, sy: svg.y }
}
function onWheel(e) {
  const d = e.deltaY > 0 ? 0.9 : 1.1
  emit('updateScale', Math.max(0.25, Math.min(2, props.scale * d)))
}
function onDrop(e) {
  const tk = window.draggingFlowNodeType
  if (tk) {
    const svg = clientToSvg(e)
    // 居中放置
    const meta = nodeTypes[tk]
    const hw = (meta?.width ?? 70) / 2
    const hh = (meta?.height ?? 62) / 2
    emit('addNode', tk, Math.max(0, svg.x - hw), Math.max(0, svg.y - hh))
    window.draggingFlowNodeType = null
  }
}
function onCanvasClick() {
  // 取消任何待处理的连线
  connectPending.value = null
  if (props.connectingFrom) emit('cancelConnect')
  emit('clearSelection')
}
function selectNode(id) { emit('selectNode', id) }
function selectEdge(id) { emit('selectEdge', id) }
function editEdge(edge) { emit('selectEdge', edge.id) }
function startConnect(id) { emit('startConnect', id) }
function editNodeProps(node) { emit('editNode', node) }
function onKeyDown(e) {
  // 忽略在input/textarea中的按键
  const tag = e.target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
  // ESC 取消正在进行的连线
  if (e.key === 'Escape') {
    connectPending.value = null
    if (props.connectingFrom) { emit('cancelConnect') }
    return
  }
  // Delete键删除选中的连线或节点
  if ((e.key === 'Delete' || e.key === 'Backspace') && (props.selectedEdgeId || props.selectedId)) {
    e.preventDefault()
    emit('deleteSelected')
  }
}
function resizeCanvas() {
  if (containerRef.value) { containerW.value = containerRef.value.clientWidth; containerH.value = containerRef.value.clientHeight }
}
onMounted(() => { resizeCanvas(); window.addEventListener('resize', resizeCanvas) })
onUnmounted(() => { window.removeEventListener('resize', resizeCanvas) })
</script>

<style scoped>
.flow-canvas-container { width: 100%; height: 100%; overflow: hidden; position: relative; background: #f9fafb; }
.flow-svg { display: block; }
.flow-edge { transition: stroke 0.15s, stroke-width 0.15s; }
.flow-edge:hover { stroke: #374151; stroke-width: 2.5; }
.selected-edge { filter: drop-shadow(0 0 3px rgba(5, 150, 105, 0.35)); }
.flow-node-group { transition: transform 0.15s; }
.flow-node-group.dragging { transition: none !important; }
.flow-node-group:hover { filter: brightness(0.98); }
.flow-node-group.selected { filter: drop-shadow(0 0 4px rgba(16, 185, 129, 0.3)); }
.connect-center-hotzone { opacity: 0; transition: opacity 0.2s; }
.connect-center-dot { opacity: 0; transition: opacity 0.2s; }
.flow-node-group:hover .connect-center-hotzone { opacity: 1; }
.flow-node-group:hover .connect-center-dot { opacity: 1; }
.flow-node-group.connecting .connect-center-hotzone { opacity: 1; }
.flow-node-group.connecting .connect-center-dot { opacity: 1; }
.flow-node-group.connect-target { filter: drop-shadow(0 0 8px rgba(16, 185, 129, 0.5)) brightness(1.05); }
.flow-node-group.connect-target rect,
.flow-node-group.connect-target polygon { stroke: #059669 !important; stroke-width: 2.5 !important; }

/* 连线右键菜单项 */
.edge-ctx-menu .ctx-item { padding: 6px 14px; cursor: pointer; display: flex; align-items: center; gap: 8px; color: #374151; transition: background 0.1s; user-select: none; }
.edge-ctx-menu .ctx-item:hover { background: #f3f4f6; }
.edge-ctx-menu .ctx-item i { width: 14px; text-align: center; color: #6b7280; }
.edge-ctx-menu .ctx-danger { color: #f87171 !important; }
.edge-ctx-menu .ctx-danger i { color: #f87171 !important; }
.edge-ctx-menu .ctx-danger:hover { background: #fef2f2; }

/* 连线模式吸附标签 */
.snap-indicator { position: fixed; pointer-events: none; background: rgba(16,185,129,0.9); color: #fff; font-size: 11px; padding: 2px 8px; border-radius: 3px; z-index: 9999; }
</style>
